<template>
  <section class="relative z-10 max-w-7xl mx-auto px-6 py-24">
    <div
      class="w-full h-px bg-linear-to-r from-transparent via-white/5 to-transparent mb-24"
    />

    <!-- REFRAMED MARKETING ENTRY HOOK -->
    <div class="flex flex-col items-center text-center gap-y-6 mb-20">
      <div
        class="inline-flex items-center gap-3 px-4 py-1.5 rounded-full bg-brand/10 border border-brand/20"
      >
        <Cpu class="size-4 text-brand" />
        <span class="text-xs font-semibold tracking-wider text-brand"
          >Built to Perform</span
        >
      </div>
      <h2
        class="text-5xl lg:text-7xl font-black text-white tracking-tighter leading-none"
      >
        Why <span class="text-brand">Pebble</span>
      </h2>
      <p
        class="text-sm lg:text-base font-medium text-white/50 leading-relaxed max-w-2xl"
      >
        Because we’re here to take the boredom out of your life! Binge
        uninterrupted on your
        <span class="text-white/80 font-semibold"
          >favourite Local shows with zero subscription fees</span
        >
        during our early launch window.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
      <!-- Feature 1: No Interruptions -->
      <div
        class="md:col-span-8 group relative overflow-hidden glass-card rounded-[2.5rem] p-10 h-[480px] border border-white/10 transition-all duration-300 hover:border-brand/40"
      >
        <div class="relative z-10 h-full flex flex-col justify-between">
          <div class="flex justify-between items-start">
            <div
              class="size-14 glass-premium rounded-2xl flex items-center justify-center border border-brand/30 shadow-[0_0_20px_rgba(255,95,0,0.1)]"
            >
              <PlayCircle class="size-7 text-brand" stroke-width="1.5" />
            </div>
            <div
              class="font-mono text-[10px] text-white/30 tracking-widest bg-white/5 px-3 py-1 rounded-full border border-white/5"
            >
              AD-FREE STREAMING
            </div>
          </div>

          <div class="max-w-md">
            <h3 class="text-3xl font-black text-white tracking-tighter mb-3">
              No Interruptions
            </h3>
            <p class="text-sm font-medium text-white/50 leading-relaxed">
              Enjoy your shows without ads or distractions. Just press play and
              stay completely immersed in your world.
            </p>
          </div>
        </div>

        <div
          class="absolute right-0 top-1/4 w-1/2 h-1/3 opacity-20 group-hover:opacity-40 transition-opacity pointer-events-none pr-10"
        >
          <div class="flex items-end gap-1.5 h-full justify-end">
            <div class="w-1 bg-brand rounded-full h-1/3 animate-pulse"></div>
            <div class="w-1 bg-brand rounded-full h-2/3"></div>
            <div class="w-1 bg-brand rounded-full h-1/2"></div>
            <div class="w-1 bg-brand rounded-full h-5/6"></div>
            <div class="w-1 bg-brand rounded-full h-4/5"></div>
          </div>
        </div>

        <div
          class="absolute -right-10 -top-10 size-80 bg-brand/5 blur-[120px] rounded-full group-hover:bg-brand/10 transition-colors pointer-events-none"
        />
      </div>

      <!-- Feature 2: Watch Offline -->
      <div
        class="md:col-span-4 group glass-card rounded-[2.5rem] p-10 h-[480px] border border-white/10 flex flex-col justify-between transition-all duration-300 hover:border-white/20"
      >
        <div class="grow flex items-center justify-center">
          <div class="relative size-40">
            <div class="absolute inset-0 bg-brand/5 blur-2xl rounded-full" />
            <div
              class="size-full rounded-full border-2 border-dashed border-white/10 flex items-center justify-center group-hover:rotate-90 transition-transform duration-1000 ease-out"
            >
              <Download
                class="size-12 text-white/30 group-hover:text-brand group-hover:scale-110 transition-all duration-300"
                stroke-width="1.5"
              />
            </div>
          </div>
        </div>
        <div>
          <h3 class="text-xl font-black text-white tracking-tighter mb-2">
            Watch Offline
          </h3>
          <p class="text-xs font-medium text-white/50 leading-relaxed">
            Download your favorites seamlessly and watch anytime, anywhere, even
            when you are entirely disconnected from the net.
          </p>
        </div>
      </div>

      <!-- Feature 3: Adaptive Quality -->
      <div
        class="md:col-span-4 group glass-card rounded-[2.5rem] p-10 h-[420px] border border-white/10 flex flex-col justify-between transition-all duration-300 hover:border-white/20"
      >
        <div class="flex justify-between items-start">
          <h3 class="text-xl font-black text-white tracking-tighter">
            Smart <br />Adaptive Quality
          </h3>
          <Zap class="size-6 text-brand" />
        </div>

        <div class="flex flex-wrap gap-2 my-auto">
          <span
            class="text-[10px] font-mono px-2.5 py-1 rounded-md bg-white/5 border border-white/5 text-white/60"
            >4K Ultra HD</span
          >
          <span
            class="text-[10px] font-mono px-2.5 py-1 rounded-md bg-brand/10 border border-brand/20 text-brand font-bold"
            >Smart Data</span
          >
          <span
            class="text-[10px] font-mono px-2.5 py-1 rounded-md bg-white/5 border border-white/5 text-white/60"
            >1080p</span
          >
        </div>

        <div class="space-y-3">
          <div
            class="flex justify-between text-xs font-mono text-brand tracking-wider"
          >
            <span>Bandwidth Optimization</span>
            <span class="font-bold">99.9%</span>
          </div>
          <div class="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
            <div
              class="h-full bg-brand w-[92%] rounded-full shadow-[0_0_12px_rgba(255,95,0,0.6)]"
            />
          </div>
        </div>
      </div>

      <!-- Feature 4: UPDATED FOR FREE EARLY ACCESS PASS -->
      <div
        class="md:col-span-8 glass-card rounded-[2.5rem] p-10 h-[420px] border border-white/10 flex flex-col lg:flex-row items-center gap-12 overflow-hidden group transition-all duration-300 hover:border-brand/30"
      >
        <div class="flex-1 space-y-5">
          <h3 class="text-3xl font-black text-white tracking-tighter">
            Free Early Access,
            <span class="text-brand">No Strings Attached</span>
          </h3>
          <p class="text-xs font-medium text-white/50 leading-relaxed">
            World-class high bitrate streaming infrastructure entirely open to
            you. Create your account today and enjoy full premium access without
            handling credit cards.
          </p>
          <PebbleAppLink
            to="/login"
            variant="glass"
            size="sm"
            class="px-8 font-bold"
          >
            Claim Free Pass
          </PebbleAppLink>
        </div>

        <div class="flex-1 relative w-full h-full min-h-[180px] lg:min-h-auto">
          <div
            class="absolute inset-0 bg-linear-to-tr from-brand/10 to-transparent blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          />
          <div
            class="relative mx-auto lg:ml-6 h-56 w-full max-w-[280px] bg-white/2 backdrop-blur-md rounded-[2rem] border border-white/10 rotate-3 translate-y-6 group-hover:rotate-0 group-hover:translate-y-2 group-hover:border-brand/30 transition-all duration-500 p-6 flex flex-col justify-between shadow-2xl"
          >
            <div class="flex justify-between items-start">
              <div>
                <p
                  class="text-[10px] font-mono text-white/40 tracking-widest uppercase"
                >
                  Beta Access Card
                </p>
                <h4 class="text-lg font-bold text-white mt-1">
                  Pebble Pioneer
                </h4>
              </div>
              <span
                class="text-xs bg-brand/20 text-brand px-2.5 py-0.5 rounded-full font-bold border border-brand/30 animate-pulse"
                >Free</span
              >
            </div>

            <div class="border-t border-white/5 pt-4 flex items-baseline gap-1">
              <span class="text-3xl font-black text-white">$0.00</span>
              <span class="text-xs text-white/40">/ forever free tier</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { Cpu, PlayCircle, Download, Zap } from "lucide-vue-next";
</script>

<style scoped>
.glass-card {
  background: rgba(255, 255, 255, 0.01);
  backdrop-filter: blur(30px) saturate(150%);
  -webkit-backdrop-filter: blur(30px) saturate(150%);
}
.glass-premium {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
}
</style>
